import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  paidMediaOverviewService,
  type PaidMediaOverviewAttributionMode,
  type PaidMediaOverviewCampaignDetailData,
  type PaidMediaOverviewGranularity,
  type PaidMediaOverviewPlatform,
} from "src/services/PaidMediaOverviewAPI";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { useAsyncResource } from "./useAsyncResource";

const CAMPAIGN_NOT_FOUND_CODE = "CAMPAIGN_NOT_FOUND";
const HTTP_NOT_FOUND = 404;

export interface UsePaidMediaOverviewCampaignDetailParams {
  platform: PaidMediaOverviewPlatform | null;
  campaignId: string | null;
  startDate: string;
  endDate: string;
  granularity: PaidMediaOverviewGranularity;
  attributionMode: PaidMediaOverviewAttributionMode;
}

export interface PaidMediaOverviewCampaignDetailBundle {
  detail: PaidMediaOverviewCampaignDetailData | null;
  currency: string | null;
  loading: boolean;
  error: string | null;
  notFound: boolean;
}

export function usePaidMediaOverviewCampaignDetail({
  platform,
  campaignId,
  startDate,
  endDate,
  granularity,
  attributionMode,
}: UsePaidMediaOverviewCampaignDetailParams): PaidMediaOverviewCampaignDetailBundle {
  const { t, i18n } = useTranslation();
  const fallbackError = t("paidMediaOverview.errors.requestFailed");
  const enabled = Boolean(platform && campaignId && startDate && endDate);
  // The drawer renders the API's `insight` copy, so it has to follow the language too.
  const lang = getApiLangFromI18n(i18n);

  const request = useCallback(() => {
    if (!platform || !campaignId) {
      return Promise.reject(new Error(fallbackError));
    }

    return paidMediaOverviewService.fetchCampaignDetail({
      platform,
      campaign_id: campaignId,
      start_date: startDate,
      end_date: endDate,
      granularity,
      attribution_mode: attributionMode,
      lang,
    });
  }, [platform, campaignId, startDate, endDate, granularity, attributionMode, lang, fallbackError]);

  const resource = useAsyncResource(enabled ? request : null, fallbackError);
  
  const notFound =
    resource.errorCode === CAMPAIGN_NOT_FOUND_CODE || resource.errorStatus === HTTP_NOT_FOUND;

  return {
    detail: resource.data?.data ?? null,
    currency: resource.data?.meta?.currency ?? null,
    loading: resource.loading,
    error: notFound ? null : resource.error,
    notFound,
  };
}
